import React from 'react'
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { ErrorX } from 'src/shared/components/icons/error-x'
import { Button } from 'src/shared/components/ui/buttons/button'

const getFriendlyErrorMessage = (status?: number) => {
  switch (status) {
    case 404:
      return 'The page or resource you requested could not be found. Please check the URL and try again.'
    case 401:
      return 'You need to be signed in to access this page. Please sign in and try again.'
    case 403:
      return 'You do not have permission to access this page. Please contact support if you believe this is a mistake.'
    case 500:
      return 'We encountered an issue on our end. Please try again later or contact support if the problem persists.'
    case 503:
      return 'Our service is temporarily unavailable. Please try again in a few minutes.'
    default:
      return 'Something unexpected happened. Please try again or contact support if the problem persists.'
  }
}

const ErrorPage: React.FC<{ title?: string; description?: string }> = ({ title, description }) => {
  const error = useRouteError()
  const navigate = useNavigate()

  // Log the actual error for debugging but don't show to user
  console.error('Error details:', error)

  let statusCode: number | undefined

  if (!title) {
    if (isRouteErrorResponse(error)) {
      statusCode = error.status
      title = statusCode ? getErrorTitle(statusCode) : title
    } else if (error instanceof Error) {
      if ('status' in error) {
        statusCode = (error as any).status
        title = statusCode ? getErrorTitle(statusCode) : title
      }
    }
  }

  if (!description) {
    description = getFriendlyErrorMessage(statusCode)
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center p-4">
      <div className="flex flex-col items-center justify-center">
        <ErrorX size={100} className="mb-4 text-muted" />
        <h1 className="mb-2 text-4xl font-bold">{title}</h1>
        <p className="mb-8 max-w-xl text-center text-lg">{description}</p>
      </div>
      <div className="flex gap-4">
        <Button onClick={() => navigate(-1)}>Go back</Button>
        <Button variant="outline" onClick={() => navigate(paths.home.getHref())}>
          Go home
        </Button>
      </div>
    </div>
  )
}

function getErrorTitle(status: number): string {
  switch (status) {
    case 404:
      return 'Page Not Found'
    case 401:
      return 'Sign In Required'
    case 403:
      return 'Access Denied'
    case 500:
      return 'Server Error'
    case 503:
      return 'Service Unavailable'
    default:
      return 'An error has occurred!'
  }
}

export default ErrorPage
